/**
 * Purpose Page
 *
 * React migration for Angular PurposeComponent.
 * Handles data fetching, pagination, and table rendering.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { PurposeTable } from './PurposeTable';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { Alert } from '../../../components/ui/Alert';
import { purposeAPI } from '../../../services/purpose-api.service';
import type {
  PurposePageProps,
  PurposeRecord,
} from '../../../types/purpose';

export const PurposePage: React.FC<PurposePageProps> = ({
  routeParams,
  queryParams,
  currentRoute,
}) => {
  console.log('[PurposePage] Mounted with props:', {
    routeParams,
    queryParams,
    currentRoute,
  });

  const [purposes, setPurposes] = useState<PurposeRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isLoading, setIsLoading] = useState(false);

  const { error, errorMessage, handleError, clearError } = useErrorHandler();

  /**
   * Fetch purposes from backend.
   */
  const fetchPurposes = useCallback(async () => {
    setIsLoading(true);
    clearError();

    try {
      const response = await purposeAPI.getPurposes({
        limit: pageSize,
        pageno: currentPage,
      });

      const rows = response?.data?.rows ?? [];
      const count = response?.data?.count ?? 0;

      setPurposes(rows);
      setTotalCount(count);
    } catch (err) {
      handleError(err, { component: 'PurposePage', action: 'fetchPurposes' });
      setPurposes([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, clearError, handleError]);

  useEffect(() => {
    fetchPurposes();
  }, [fetchPurposes]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  return (
    <div className="equal-ds-root h-full flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold text-text-primary">
          Purpose
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          View AA purpose configurations.
        </p>
      </header>

      {errorMessage && (
        <Alert variant="error">
          <div className="flex flex-col gap-1">
            <span>{errorMessage}</span>
            {error?.message && (
              <span className="text-xs opacity-80">{error.message}</span>
            )}
          </div>
        </Alert>
      )}

      <PurposeTable
        data={purposes}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        isLoading={isLoading}
      />
    </div>
  );
};
